import { useMemo, type ReactNode } from "react";
import { DocHtmlView, isDocHtml } from "../DocHtml";
import type { DocContext } from "./doc-context";

// SAFE "Launchpad doc" reader for interface documents (audit, plan, emails,
// webinar). Builds React nodes from text - no raw HTML ever reaches the DOM.
// Format conventions (generator contract, dfy2 references):
//   `Text {.lead}`      -> standfirst paragraph
//   `!!! klic Text`     -> callout (klic|pozor|tip|ok)
//   `::: stats` block   -> stat tiles, one `Label | Value [| note]` per line
//   `??? Title` ... `???` -> collapsible block (e-mail bodies)
//   headings, lists, pipe tables, **bold**, > quote
function inlineMd(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Generated docs use `code spans` for file names and commands; without this
  // branch the reader printed the raw backticks (fourth-pass finding).
  // ==mark== is the amber key-phrase highlight, *em* plain emphasis; bold
  // stays first in the alternation so ** never half-matches as *em*.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|==[^=\s][^=]*==|\*[^*\s][^*]*\*)/g);
  parts.forEach((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      nodes.push(<strong key={`${keyPrefix}-b${index}`}>{part.slice(2, -2)}</strong>);
    } else if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      nodes.push(<code key={`${keyPrefix}-c${index}`}>{part.slice(1, -1)}</code>);
    } else if (part.startsWith("==") && part.endsWith("==") && part.length > 4) {
      nodes.push(<mark key={`${keyPrefix}-m${index}`}>{part.slice(2, -2)}</mark>);
    } else if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      nodes.push(<em key={`${keyPrefix}-e${index}`}>{part.slice(1, -1)}</em>);
    } else if (part) {
      nodes.push(part);
    }
  });
  return nodes;
}

const CALLOUT_TYPES: Record<string, string> = { klic: "key", pozor: "warn", tip: "tip", ok: "ok" };

// The `:::` fence family (design language T6a): every fence is
// `::: kind [header]`, data lines inside, bare `:::` closes. Unknown kinds
// fall through as plain text so an old renderer never eats content.
const FENCE_KINDS = ["stats", "cislo", "srovnani", "kroky", "mereni", "paleta"] as const;
// `::: paleta` rows are `Název | #HEX [| k čemu]`; only a valid hex renders a
// swatch, anything else degrades to the label/value text (never a blank chip).
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
type FenceKind = (typeof FENCE_KINDS)[number];
const CHECK_ITEM = /^\[( |x|X)\]\s+(.*)$/;

// First number in a gauge value ("42 %", "4,8 z 5") drives the bar width,
// clamped to 0..100; no number = no bar, the row still renders.
export function meterPercent(value: string): number | null {
  const match = /(\d+(?:[.,]\d+)?)/.exec(value);
  if (!match) return null;
  const parsed = Number.parseFloat(match[1].replace(",", "."));
  if (!Number.isFinite(parsed)) return null;
  return Math.min(Math.max(parsed, 0), 100);
}

export function MarkdownView(props: { md: string; onNavigate?: never; sectionStart?: number; nested?: boolean; docHtml?: boolean; docContext?: DocContext }) {
  // Documents authored in the HTML component language (Pavel 2026-08-03) route
  // here too, so every existing call site - reader, full-screen overlay,
  // version diff, staff console - gets them without changing a line. Markdown
  // documents are every document delivered before today and keep this path.
  // The diff view renders FRAGMENTS of a document, which no longer start with
  // <doc>, so it passes the answer in explicitly.
  const docHtml = props.docHtml ?? isDocHtml(props.md);
  const blocks = useMemo(() => {
    if (docHtml) return [] as ReactNode[];
    const lines = props.md.replace(/\r\n/g, "\n").split("\n");
    const out: ReactNode[] = [];
    let paragraph: string[] = [];
    let list: { ordered: boolean; items: string[] } | null = null;
    let table: string[][] | null = null;
    let fence: { kind: FenceKind; header: string; lines: string[] } | null = null;
    let quote: { lines: string[]; cite?: string } | null = null;
    let collapsible: { title: string; lines: string[] } | null = null;
    // The diff view renders a document in pieces; sectionStart keeps the
    // heading numbers continuous across those pieces.
    let sectionIndex = props.sectionStart ?? 0;
    const flushParagraph = () => {
      if (paragraph.length) {
        const joined = paragraph.join(" ");
        const lead = /\{\.lead\}\s*$/.test(joined);
        const clean = joined.replace(/\s*\{\.lead\}\s*$/, "");
        out.push(<p key={`p${out.length}`} className={lead ? "doc-lead" : undefined}>{inlineMd(clean, `p${out.length}`)}</p>);
        paragraph = [];
      }
    };
    const flushList = () => {
      if (list) {
        // `- [ ]` / `- [x]` items turn a plain bullet list into a checklist.
        const isChecklist = !list.ordered && list.items.some((item) => CHECK_ITEM.test(item));
        const items = list.items.map((item, index) => {
          const check = isChecklist ? CHECK_ITEM.exec(item) : null;
          if (check) {
            const done = check[1].toLowerCase() === "x";
            return (
              <li key={index} className={`doc-check${done ? " doc-check-done" : ""}`}>
                <span className="doc-check-box" aria-hidden="true">{done ? "✓" : ""}</span>
                <span className="doc-check-text">{inlineMd(check[2], `li${index}`)}</span>
              </li>
            );
          }
          return <li key={index}>{inlineMd(item, `li${index}`)}</li>;
        });
        out.push(list.ordered
          ? <ol key={`l${out.length}`}>{items}</ol>
          : <ul key={`l${out.length}`} className={isChecklist ? "doc-checklist" : undefined}>{items}</ul>);
        list = null;
      }
    };
    const flushTable = () => {
      if (table && table.length) {
        const [head, ...rows] = table;
        out.push(
          <div className="gate-doc-table-wrap" key={`t${out.length}`}>
            <table>
              <thead><tr>{head.map((cell, index) => <th key={index}>{inlineMd(cell, `th${index}`)}</th>)}</tr></thead>
              <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, index) => <td key={index}>{inlineMd(cell, `td${rowIndex}-${index}`)}</td>)}</tr>)}</tbody>
            </table>
          </div>
        );
        table = null;
      }
    };
    const flushQuote = () => {
      if (quote && (quote.lines.length || quote.cite)) {
        out.push(
          <blockquote key={`q${out.length}`}>
            {quote.lines.map((entry, index) => <p key={index}>{inlineMd(entry, `q${out.length}-${index}`)}</p>)}
            {quote.cite && <cite>{inlineMd(quote.cite, `qc${out.length}`)}</cite>}
          </blockquote>
        );
      }
      quote = null;
    };
    const flushFence = () => {
      if (!fence) return;
      const { kind, header, lines: body } = fence;
      fence = null;
      // stats/cislo/kroky/mereni share the `A | B [| C]` line shape.
      const rows = body
        .map((entry) => entry.split("|").map((cell) => cell.trim()).filter(Boolean))
        .filter((cells) => cells.length >= 2);
      if (kind === "stats" && rows.length) {
        out.push(
          <div className="doc-stats" key={`s${out.length}`}>
            {rows.map((cells, index) => (
              <div className="doc-stat" key={index}>
                <span className="doc-stat-value" data-long={cells[1] && cells[1].length > 14 ? "" : undefined}>{cells[1]}</span>
                <span className="doc-stat-label">{cells[0]}</span>
                {cells[2] && <span className="doc-stat-note">{cells[2]}</span>}
              </div>
            ))}
          </div>
        );
      } else if (kind === "cislo" && rows.length) {
        for (const cells of rows) {
          out.push(
            <div className="doc-hero" key={`hero${out.length}`}>
              <span className="doc-hero-value">{cells[0]}</span>
              <span className="doc-hero-claim">{inlineMd(cells[1], `hero${out.length}`)}</span>
              {cells[2] && <span className="doc-hero-note">{inlineMd(cells[2], `heron${out.length}`)}</span>}
            </div>
          );
        }
      } else if (kind === "kroky" && rows.length) {
        out.push(
          <ol className="doc-steps" key={`st${out.length}`}>
            {rows.map((cells, index) => (
              <li className="doc-step" key={index}>
                <span className="doc-step-marker" aria-hidden="true">{index + 1}</span>
                <span className="doc-step-label">{cells[0]}</span>
                <span className="doc-step-title">{inlineMd(cells[1], `stt${index}`)}</span>
                {cells[2] && <span className="doc-step-detail">{inlineMd(cells[2], `std${index}`)}</span>}
              </li>
            ))}
          </ol>
        );
      } else if (kind === "mereni" && rows.length) {
        out.push(
          <div className="doc-meters" key={`m${out.length}`}>
            {rows.map((cells, index) => {
              const percent = meterPercent(cells[1]);
              return (
                <div className="doc-meter" key={index}>
                  <span className="doc-meter-label">{inlineMd(cells[0], `ml${index}`)}</span>
                  <span className="doc-meter-value">{cells[1]}</span>
                  {percent !== null && (
                    <div className="doc-meter-track" aria-hidden="true">
                      <div className="doc-meter-fill" style={{ width: `${percent}%` }} />
                    </div>
                  )}
                  {cells[2] && <span className="doc-meter-note">{inlineMd(cells[2], `mn${index}`)}</span>}
                </div>
              );
            })}
          </div>
        );
      } else if (kind === "paleta" && rows.length) {
        out.push(
          <div className="doc-palette" key={`pal${out.length}`}>
            {rows.map((cells, index) => {
              const hex = cells[1];
              const valid = HEX_COLOR.test(hex);
              return (
                <div className="doc-swatch" key={index}>
                  <span
                    className="doc-swatch-chip"
                    style={valid ? { background: hex } : undefined}
                    data-empty={valid ? undefined : "1"}
                    aria-hidden="true"
                  />
                  <span className="doc-swatch-name">{cells[0]}</span>
                  <span className="doc-swatch-hex">{hex}</span>
                  {cells[2] && <span className="doc-swatch-note">{cells[2]}</span>}
                </div>
              );
            })}
          </div>
        );
      } else if (kind === "srovnani") {
        // Two columns split by `---`; header names them (`Dnes | S Buddym`).
        const [beforeTitle, afterTitle] = header.split("|").map((part) => part.trim());
        const columns: string[][] = [[], []];
        let side = 0;
        for (const entry of body) {
          const item = entry.trim();
          if (!item) continue;
          if (/^-{3,}$/.test(item)) { side = 1; continue; }
          columns[side].push(item.replace(/^[-*]\s+/, ""));
        }
        out.push(
          <div className="doc-compare" key={`cmp${out.length}`}>
            {columns.map((items, columnIndex) => (
              <div className={`doc-compare-col ${columnIndex === 0 ? "doc-compare-before" : "doc-compare-after"}`} key={columnIndex}>
                {(columnIndex === 0 ? beforeTitle : afterTitle) && (
                  <p className="doc-compare-head">{columnIndex === 0 ? beforeTitle : afterTitle}</p>
                )}
                <ul>{items.map((item, index) => <li key={index}>{inlineMd(item, `cmp${columnIndex}-${index}`)}</li>)}</ul>
              </div>
            ))}
          </div>
        );
      }
    };
    const flushAll = () => { flushParagraph(); flushList(); flushTable(); flushFence(); flushQuote(); };
    for (const raw of lines) {
      const line = raw.trimEnd();
      if (collapsible) {
        if (/^\?\?\?\s*$/.test(line)) {
          out.push(
            <details className="doc-fold" key={`f${out.length}`}>
              <summary>{inlineMd(collapsible.title, `ft${out.length}`)}</summary>
              <MarkdownView md={collapsible.lines.join("\n")} nested />
            </details>
          );
          collapsible = null;
        } else {
          collapsible.lines.push(raw);
        }
        continue;
      }
      if (fence !== null) {
        if (/^:::\s*$/.test(line)) flushFence();
        else fence.lines.push(line);
        continue;
      }
      const fold = /^\?\?\?\s+(.+)$/.exec(line);
      if (fold) { flushAll(); collapsible = { title: fold[1], lines: [] }; continue; }
      const fenceOpen = /^:::\s*(\w+)\s*(.*)$/.exec(line);
      if (fenceOpen && (FENCE_KINDS as readonly string[]).includes(fenceOpen[1])) {
        flushAll();
        fence = { kind: fenceOpen[1] as FenceKind, header: fenceOpen[2] ?? "", lines: [] };
        continue;
      }
      const callout = /^!!!\s+(\w+)\s+(.+)$/.exec(line);
      if (callout && CALLOUT_TYPES[callout[1]]) {
        flushAll();
        out.push(
          <div className={`doc-callout doc-callout-${CALLOUT_TYPES[callout[1]]}`} key={`c${out.length}`}>
            {inlineMd(callout[2], `c${out.length}`)}
          </div>
        );
        continue;
      }
      const heading = /^(#{1,4})\s+(.*)$/.exec(line);
      if (heading) {
        flushAll();
        const level = heading[1].length;
        const content = inlineMd(heading[2], `h${out.length}`);
        // `##` = chapter opener (numbered, chapter nav target). Inside a fold
        // (nested) a `##` is body structure, not a chapter - render modestly.
        if (level <= 2 && !props.nested) {
          sectionIndex += 1;
          out.push(
            <h3 key={`h${out.length}`} className="doc-section">
              <span className="doc-section-num" aria-hidden="true">{String(sectionIndex).padStart(2, "0")}</span>
              {content}
            </h3>
          );
        } else {
          out.push(<h4 key={`h${out.length}`}>{content}</h4>);
        }
        continue;
      }
      // `*** Kicker` = section divider (bare *** = plain rule). Checked before
      // bullets: `***` has no space after the first `*`, but keep it explicit.
      const divider = /^\*{3,}\s*(.*)$/.exec(line);
      if (divider) {
        flushAll();
        out.push(
          <div className="doc-divider" role="separator" key={`d${out.length}`}>
            {divider[1] ? <span>{inlineMd(divider[1], `d${out.length}`)}</span> : null}
          </div>
        );
        continue;
      }
      if (/^>\s?/.test(line)) {
        // Consecutive `>` lines group into ONE quote; a trailing `> -- Name`
        // line becomes the attribution.
        flushParagraph(); flushList(); flushTable(); flushFence();
        const content = line.replace(/^>\s?/, "").trim();
        quote = quote ?? { lines: [] };
        const cite = /^--\s*(.+)$/.exec(content);
        if (cite) quote.cite = cite[1];
        else if (content) quote.lines.push(content);
        continue;
      }
      if (/^\s*\|/.test(line)) {
        flushParagraph(); flushList(); flushQuote();
        const cells = line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((cell) => cell.trim());
        if (cells.every((cell) => /^:?-{2,}:?$/.test(cell))) continue;
        table = table ?? [];
        table.push(cells);
        continue;
      }
      const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
      const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
      if (bullet || numbered) {
        flushParagraph(); flushTable(); flushQuote();
        const ordered = Boolean(numbered);
        if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
        list.items.push((bullet ?? numbered)![1]);
        continue;
      }
      if (!line.trim()) { flushAll(); continue; }
      flushList(); flushTable(); flushQuote();
      paragraph.push(line.trim());
    }
    if (collapsible) {
      out.push(
        <details className="doc-fold" key={`f${out.length}`}>
          <summary>{inlineMd(collapsible.title, `ft${out.length}`)}</summary>
          <MarkdownView md={collapsible.lines.join("\n")} nested />
        </details>
      );
    }
    flushAll();
    return out;
  }, [props.md, props.sectionStart, props.nested, docHtml]);
  if (docHtml) {
    return (
      <div className="gate-doc-md gate-doc-html">
        {/* A fragment (one e-mail, one diff block) cannot see the document's
            segment legend or its fold ids; the splitter passes them in. */}
        <DocHtmlView html={props.md} sectionStart={props.sectionStart} docContext={props.docContext} />
      </div>
    );
  }
  return <div className="gate-doc-md">{blocks}</div>;
}
